/**
 * DeviceToken Service
 *
 * Manages anonymous device authentication tokens.
 * Tokens are generated automatically and used to authenticate devices
 * with the backend after enrollment and approval.
 *
 * - Tokens are persisted in localStorage
 * - Tokens can be revoked by the server (returns 401)
 * - Authentication failures preserve the credential for controlled recovery
 */

import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = '@efflux:deviceToken';
const LOG_PREFIX = '[DeviceToken]';

class DeviceTokenService {
  private token: string | null = null;

  /**
   * Gets the current device token.
   * Generates a new one if it doesn't exist.
   */
  getToken(): string {
    if (this.token) {
      this.shareWithNativeHost(this.token);
      return this.token;
    }

    // Try to load from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      console.log(`${LOG_PREFIX} ✅ Loaded existing token from storage`);
      this.token = stored;
      this.shareWithNativeHost(stored);
      return stored;
    }

    // Generate new token
    return this.generateToken();
  }

  /**
   * Generates a new device token and persists it.
   */
  generateToken(): string {
    const newToken = uuidv4();
    this.token = newToken;
    localStorage.setItem(STORAGE_KEY, newToken);
    this.shareWithNativeHost(newToken);
    console.log(`${LOG_PREFIX} Generated new device credential`);
    return newToken;
  }

  /** Local reset only; existing devices require authenticated server-side recovery. */
  refreshToken(): string {
    console.log(
      `${LOG_PREFIX} Refreshing local credential — authenticated recovery required`,
    );
    return this.generateToken();
  }

  private shareWithNativeHost(token: string): void {
    const host = typeof window === 'undefined' ? undefined : window.EffluxHost;
    void Promise.resolve(host?.setDeviceToken?.(token)).catch(() => {
      console.warn(
        `${LOG_PREFIX} Native identity requires authenticated recovery`,
      );
    });
  }

  /**
   * Clears the device token (for testing/reset).
   */
  clearToken(): void {
    this.token = null;
    localStorage.removeItem(STORAGE_KEY);
    console.log(`${LOG_PREFIX} 🗑️ Cleared device token`);
  }

  /**
   * Gets the current token without generating a new one.
   * Returns null if no token exists.
   */
  getTokenOrNull(): string | null {
    return this.token || localStorage.getItem(STORAGE_KEY);
  }
}

export const deviceTokenService = new DeviceTokenService();
export default deviceTokenService;
